/* ============================================================
   What is in the cart.

   The cart is a plain list of strings so that it stays easy to reason about
   and easy to hand to the server. Two shapes live in it:

     "7f3c…"                     an original painting, by its id
     "shop:<itemId>:<variantId>" one size of one print / sticker / bookmark

   A painting can only appear once — there is only one of it. A shop line may
   appear several times, and that repetition IS the quantity: three identical
   strings means three stickers. Everything that needs to display or price the
   cart goes through `cartLines` below, so the two pages, the order record and
   the server all agree on what a cart means.
   ============================================================ */

import { thumbImg, kindLabel, hasSizes } from "../data";

export const SHOP_PREFIX = "shop:";

export const shopToken = (itemId, variantId) => `${SHOP_PREFIX}${itemId}:${variantId}`;

export const isShopToken = (token) => typeof token === "string" && token.startsWith(SHOP_PREFIX);

/* "shop:abc:a4" -> { itemId: "abc", variantId: "a4" }.
   Split from the right so an id containing a colon still parses. */
export function parseShopToken(token) {
  if (!isShopToken(token)) return null;
  const rest = token.slice(SHOP_PREFIX.length);
  const cut = rest.lastIndexOf(":");
  if (cut <= 0) return null;
  return { itemId: rest.slice(0, cut), variantId: rest.slice(cut + 1) };
}

export const findVariant = (item, variantId) =>
  ((item && item.variants) || []).find((v) => String(v.id) === String(variantId)) || null;

/* Turn the raw token list into rows ready to render or price.

   Anything that can no longer be found — a painting deleted while someone had
   it in their cart, a size the artist removed — is dropped rather than shown as
   a broken row. `lines` is what the buyer sees; the caller can compare its
   length against the cart's to notice that something vanished. */
export function cartLines(cart, paintings, shopItems) {
  const byPainting = new Map((paintings || []).map((p) => [p.id, p]));
  const byItem = new Map((shopItems || []).map((s) => [s.id, s]));
  const lines = [];
  const seen = new Map(); /* token -> index in lines, so repeats become a quantity */

  for (const token of cart || []) {
    if (seen.has(token)) { lines[seen.get(token)].qty += 1; continue; }

    if (isShopToken(token)) {
      const parsed = parseShopToken(token);
      const item = parsed && byItem.get(parsed.itemId);
      const variant = item && findVariant(item, parsed.variantId);
      if (!item || !variant) continue;
      seen.set(token, lines.length);
      lines.push({
        token, kind: "shop", qty: 1,
        id: item.id, variantId: variant.id,
        title: item.title,
        /* "Print · A3 — 30 × 42 cm" for a print; just "Sticker" where there was
           never a size to choose, rather than a meaningless "Standard". */
        sub: [kindLabel(item.kind).replace(/s$/, ""), hasSizes(item.kind) ? variant.label : null]
          .filter(Boolean).join(" · "),
        price: Number(variant.price) || 0,
        image: thumbImg((item.images || [])[0]),
        href: `/prints/${encodeURIComponent(item.id)}`,
      });
      continue;
    }

    const art = byPainting.get(token);
    if (!art) continue;
    seen.set(token, lines.length);
    lines.push({
      token, kind: "painting", qty: 1,
      id: art.id, variantId: null,
      title: art.title,
      sub: [art.dims, "original"].filter(Boolean).join(" · "),
      price: Number(art.price) || 0,
      image: thumbImg((art.images || [])[0]),
      href: `/painting/${encodeURIComponent(art.id)}`,
    });
  }
  return lines;
}

export const cartTotal = (lines) => lines.reduce((sum, l) => sum + l.price * l.qty, 0);

/* The shape stored on an order row and shown in the Studio. */
export const orderItems = (lines) => lines.map((l) => ({
  id: l.id,
  title: l.title + (l.kind === "shop" ? ` (${l.sub})` : ""),
  price: l.price,
  qty: l.qty,
  kind: l.kind,
  ...(l.variantId ? { variant: l.variantId } : {}),
}));
